import type { Store } from '../types/location.types';
import { API_CONFIG, CMS_BASE_URL, CMS_ENVIRONMENT_QUERY } from '../config/api.config';
import { getCurrentDomain } from '../utils/domain';
import type { DepositSettings } from '../utils/depositPreview';

// Cache for stores data with TTL
const STORE_CACHE_KEY = 'store_cache';
const COMPANY_LOGO_CACHE_KEY = 'company_logo_cache';
const COMPANY_NAME_CACHE_KEY = 'company_name_cache';
const STORE_CACHE_TTL = 30 * 60 * 1000; // 30 minutes

interface StoreCache {
  data: Store[];
  companyPublicLogoUrl?: string;
  /** MPG Perks tenant opt-out; absent (older cache) reads as on. */
  perksEnabled?: boolean;
  /** False once the salon's plan has ended; absent (older cache) reads as taking bookings. */
  acceptingOnlineBookings?: boolean;
  companyName?: string;
  contactNumbers?: Record<string, string>;
  /** The salon's deposit settings, for the services step's cart (SF 60). Absent in an older cache. */
  deposit?: DepositSettings;
  timestamp: number;
}

const readCache = (): StoreCache | null => {
  try {
    const cached = localStorage.getItem(STORE_CACHE_KEY);
    return cached ? (JSON.parse(cached) as StoreCache) : null;
  } catch {
    return null;
  }
};

/**
 * The lookup under way. Several readers ask on a cold load (the breed
 * groups, the offers bar, the booking pages); they share one read.
 */
let lookup: Promise<Store[]> | null = null;

// Build a storeId -> main-contact mobile map from the company payload. The
// backend surfaces the main contact's number directly as
// store.mainContactMobileNumber (only that one number, not all staff). Falls
// back to resolving from nested store.users for older payloads.
const buildContactNumbers = (data: any): Record<string, string> => {
  const map: Record<string, string> = {};
  const stores: any[] = data?.stores || [];
  stores.forEach((store) => {
    const direct = store?.mainContactMobileNumber;
    if (direct) {
      map[String(store.id)] = direct;
      return;
    }
    const contactId = store?.mainContactUserId;
    if (!contactId) return;
    const users: any[] = store?.users || data?.users || [];
    const contact = users.find((u) => u?.id === contactId);
    const raw =
      typeof contact?.mobileNumber === 'string'
        ? contact.mobileNumber
        : contact?.mobileNumber?.number;
    if (raw) map[String(store.id)] = raw;
  });
  return map;
};

export const storeService = {
  getStores: async (forceRefresh = false): Promise<Store[]> => {
    try {
      // Check cache first unless force refresh
      if (!forceRefresh) {
        const cached = localStorage.getItem(STORE_CACHE_KEY);
        if (cached) {
          try {
            const cacheData: StoreCache = JSON.parse(cached);
            const now = Date.now();
            // Return cached data if still fresh
            if (now - cacheData.timestamp < STORE_CACHE_TTL) {
              return cacheData.data;
            }
          } catch (e) {
            // Invalid cache, proceed to fetch
            console.warn('Invalid store cache, fetching fresh data');
          }
        }
      }

      if (!lookup) {
        lookup = storeService.lookUp().finally(() => {
          lookup = null;
        });
      }
      return await lookup;
    } catch (error) {
      console.error('Error fetching stores:', error);

      // On error, try to return stale cache data if available
      const cached = localStorage.getItem(STORE_CACHE_KEY);
      if (cached) {
        try {
          const cacheData: StoreCache = JSON.parse(cached);
          return cacheData.data;
        } catch (e) {
          // Can't use cache, throw original error
        }
      }

      throw error;
    }
  },

  /** One read of the tenant lookup, cached (see getStores). */
  lookUp: async (): Promise<Store[]> => {
    // Use URL-encoded domain name per requirement
    const url = `${CMS_BASE_URL}/api/companies/by-domain-name?domainName=${getCurrentDomain()}${CMS_ENVIRONMENT_QUERY}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: API_CONFIG.headers
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch stores: ${response.status}`);
    }

    const data = await response.json();
    const stores = data?.stores || [];
    const companyPublicLogoUrl = data?.companyPublicLogoUrl || '';
    const companyName = data?.name || data?.companyName || '';

    // Cache the result (stores + logo URL + company name + contact numbers + deposit settings)
    const cacheData: StoreCache = {
      data: stores,
      companyPublicLogoUrl,
      companyName,
      perksEnabled: data?.perksEnabled !== false,
      acceptingOnlineBookings: data?.acceptingOnlineBookings !== false,
      contactNumbers: buildContactNumbers(data),
      deposit: {
        depositEnabled: data?.depositEnabled,
        depositPercent: data?.depositPercent,
        depositType: data?.depositType,
        depositFixedAmount: data?.depositFixedAmount
      },
      timestamp: Date.now()
    };
    localStorage.setItem(STORE_CACHE_KEY, JSON.stringify(cacheData));

    // Also cache logo URL and company name separately for quick access
    if (companyPublicLogoUrl) {
      localStorage.setItem(COMPANY_LOGO_CACHE_KEY, companyPublicLogoUrl);
    }
    if (companyName) {
      localStorage.setItem(COMPANY_NAME_CACHE_KEY, companyName);
    }

    return stores;
  },

  /**
   * The salon's deposit settings, from the same lookup (the services step's
   * cart, SF 60). The cart read the lookup a second time for them on every
   * visit. A cache written before they were kept is read afresh once. Null
   * when they can't be read: the cart then shows no deposit line, and Review
   * and book reads them again before Pay.
   */
  getDepositSettings: async (): Promise<DepositSettings | null> => {
    try {
      const cached = readCache();
      const fresh = !!cached && Date.now() - cached.timestamp < STORE_CACHE_TTL;
      if (fresh && cached?.deposit) return cached.deposit;
      await storeService.getStores(fresh);
      return readCache()?.deposit ?? null;
    } catch {
      return null;
    }
  },

  /**
   * This storefront's business, from its own domain lookup (the stores it
   * shows). Every sign-in, sign-up and Google redirect must name it.
   *
   * It used to be API_CONFIG.companyId — hard-coded '1' — so on any second
   * salon's storefront a customer signing in was signed in to business 1:
   * Google sent them back to business 1's site, Register created them there,
   * and email sign-in joined them to it (Lena, 28 Sep 2026). Production only
   * worked because Kloud9pet happens to be business 1.
   *
   * Throws rather than guessing: signing a customer in to the wrong salon is
   * worse than asking them to try again.
   */
  getCompanyId: async (): Promise<number> => {
    const stores = await storeService.getStores();
    const id = Number(stores.find((s) => s?.companyId)?.companyId);
    if (!Number.isInteger(id) || id <= 0) throw new Error('COMPANY_UNKNOWN');
    return id;
  },

  // Clear store cache (useful for debugging or forced refresh)
  clearCache: () => {
    localStorage.removeItem(STORE_CACHE_KEY);
    localStorage.removeItem(COMPANY_LOGO_CACHE_KEY);
    localStorage.removeItem(COMPANY_NAME_CACHE_KEY);
  },

  // Get a store's main-contact mobile number (for "call the store" actions).
  // Ensures the company data is loaded, then resolves from the cached map.
  getStoreContactNumber: async (storeId: string | number): Promise<string | null> => {
    try {
      await storeService.getStores();
      const cached = localStorage.getItem(STORE_CACHE_KEY);
      if (!cached) return null;
      const cacheData: StoreCache = JSON.parse(cached);
      return cacheData.contactNumbers?.[String(storeId)] || null;
    } catch (e) {
      return null;
    }
  },

  /**
   * Whether this tenant shows MPG Perks. On unless the tenant record says
   * otherwise — and on while nothing is cached yet, so the menu never
   * flickers the entry away on a cold load.
   */
  getPerksEnabled: (): boolean => {
    const cached = localStorage.getItem(STORE_CACHE_KEY);
    if (!cached) return true;
    try {
      const cacheData: StoreCache = JSON.parse(cached);
      return cacheData.perksEnabled !== false;
    } catch (e) {
      return true;
    }
  },

  /**
   * Whether this salon is taking online bookings. False only when its plan
   * has ended (the admin's cancellation policy); a missing value — an older
   * cache, or the lookup not run yet — reads as true so a live salon is
   * never wrongly closed by a cache miss.
   */
  isAcceptingOnlineBookings: (): boolean => {
    const cached = localStorage.getItem(STORE_CACHE_KEY);
    if (!cached) return true;
    try {
      const cacheData: StoreCache = JSON.parse(cached);
      return cacheData.acceptingOnlineBookings !== false;
    } catch (e) {
      return true;
    }
  },

  /**
   * Whether a customer can book this location online: the business is
   * taking bookings, and so is the location (parked, plan ended and payouts
   * not set up all say no; mpgadmin D17). A missing value reads as yes, as
   * above: the booking itself is still refused, and then says so.
   */
  canBookOnline: (store?: Pick<Store, 'id' | 'acceptingOnlineBookings'> | null): boolean => {
    if (!storeService.isAcceptingOnlineBookings()) return false;
    if (!store) return true;
    // The chosen location is a snapshot saved when it was picked; the
    // lookup's copy is at most 30 minutes old. A salon that has since set
    // up payouts must not stay "closed" for a returning customer.
    const current = storeService.getCachedStores().find((s) => s.id === store.id) ?? store;
    return current.acceptingOnlineBookings !== false;
  },

  /** The locations from the last lookup, without fetching. Empty when none is cached. */
  getCachedStores: (): Store[] => {
    try {
      const cached = localStorage.getItem(STORE_CACHE_KEY);
      if (!cached) return [];
      const cacheData: StoreCache = JSON.parse(cached);
      return Array.isArray(cacheData.data) ? cacheData.data : [];
    } catch (e) {
      return [];
    }
  },

  // Get cached company logo URL
  getCompanyLogoUrl: (): string | null => {
    // Try quick access cache first
    const logoUrl = localStorage.getItem(COMPANY_LOGO_CACHE_KEY);
    if (logoUrl) return logoUrl;

    // Fallback to store cache
    const cached = localStorage.getItem(STORE_CACHE_KEY);
    if (cached) {
      try {
        const cacheData: StoreCache = JSON.parse(cached);
        return cacheData.companyPublicLogoUrl || null;
      } catch (e) {
        return null;
      }
    }
    return null;
  },

  // Get cached company name
  getCompanyName: (): string | null => {
    // Try quick access cache first
    const name = localStorage.getItem(COMPANY_NAME_CACHE_KEY);
    if (name) return name;

    // Fallback to store cache
    const cached = localStorage.getItem(STORE_CACHE_KEY);
    if (cached) {
      try {
        const cacheData: StoreCache = JSON.parse(cached);
        return cacheData.companyName || null;
      } catch (e) {
        return null;
      }
    }
    return null;
  }
};
